import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import {
  Box,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';

import styles from './qr-code-generator.module.scss';

interface QRCodeGeneratorProps {
  gameUrl: string;
  gameBuildId?: string;
  showGameLink: boolean;
}

const QRCodeGenerator: FC<QRCodeGeneratorProps> = ({
  gameUrl,
  gameBuildId,
  showGameLink,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');

  const generateQRCode = async (url: string) => {
    if (!url) return;

    setIsGenerating(true);
    setError('');

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Очищаем canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      // Генерируем QR-код
      const dataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
        errorCorrectionLevel: 'M',
      });

      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Ошибка генерации QR-кода:', err);
      setError(t('qrCode.generationError') || 'Ошибка генерации QR-кода');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const link = document.createElement('a');
    link.download = `qr-code-${gameBuildId || 'game'}.png`;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (showGameLink && gameUrl) {
      generateQRCode(gameUrl);
    }
  }, [showGameLink, gameUrl]);

  if (!showGameLink || !gameUrl) {
    return null;
  }

  return (
    <Box className={styles.qrCodeContainer}>
      <Paper 
        elevation={3} 
        className={styles.qrCodePaper}
        sx={{
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        }}
      >
        <Box className={styles.qrCodeHeader}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <QrCodeIcon sx={{ color: '#1976d2', fontSize: '1.5rem' }} />
            <Typography variant="h6" className={styles.qrCodeTitle}>
              {t('qrCode.title') || 'QR-код для игры'}
            </Typography>
          </Stack>
          <Tooltip title={t('qrCode.refresh') || 'Обновить QR-код'}>
            <IconButton
              onClick={() => generateQRCode(gameUrl)}
              disabled={isGenerating}
              size="small"
              sx={{ color: '#1976d2' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className={styles.qrCodeContent}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <AlertTitle>{t('qrCode.error') || 'Ошибка'}</AlertTitle>
              {error}
            </Alert>
          )}

          {isGenerating ? (
            <Box className={styles.loadingContainer}>
              <Typography variant="body2" color="text.secondary">
                {t('qrCode.generating') || 'Генерируем QR-код...'}
              </Typography>
            </Box>
          ) : qrCodeDataUrl ? (
            <Stack spacing={2} alignItems="center">
              <Box className={styles.qrCodeImageContainer}>
                <img
                  src={qrCodeDataUrl}
                  alt="QR-код для игры"
                  className={styles.qrCodeImage}
                />
              </Box>
              
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadQRCode}
                  className={styles.downloadButton}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  {t('qrCode.download') || 'Скачать QR-код'}
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" textAlign="center">
                {t('qrCode.scanHint') || 'Отсканируйте QR-код для быстрого доступа к игре'}
              </Typography>
            </Stack>
          ) : (
            <Box className={styles.emptyContainer}>
              <Typography variant="body2" color="text.secondary">
                {t('qrCode.noData') || 'QR-код не сгенерирован'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Скрытый canvas для генерации */}
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
          width={256}
          height={256}
        />
      </Paper>
    </Box>
  );
};

export default QRCodeGenerator;

